import React, {useCallback, useMemo, useState} from "react"
import {Button, Checkbox, Modal, Popconfirm, TimePicker} from "antd"
import {useFormik} from "formik"
import * as yup from "yup"
import dayjs from "dayjs"
import {getDay, securityScheduleOptions} from "../../../helps/scheduleHelper"
import {FormSelect} from "../../../components/Form/FormSelect"
import {FormTextarea} from "../../../components/Form/FormTextarea"
import {changeObjectSchedule, createObjectSchedule, deleteObjectSchedule} from "../../../store/objectScheduleReducer"

const format = 'HH:mm'

const schema = yup.object().shape({
    days: yup.array().min(1, 'Выберите хотя бы 1 день'),
    time: yup.object().nullable(true).required('Выберите время'),
    comment: yup.string().max(1000, 'Максимальная длина комментария 1000 символов'),
})

type PropsType = {
    idObject: number
    selectedSchedule?: any
    setSelectedSchedule?: any
    showModalSchedule: boolean
    setShowModalSchedule: (value: boolean) => void
    dispatch: any
    controller: AbortController
}
export const ObjectScheduleForm: React.FC<PropsType> = ({selectedSchedule, setSelectedSchedule, idObject, showModalSchedule, setShowModalSchedule, dispatch, controller}) => {
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const formik = useFormik({
        initialValues: {
            security: selectedSchedule ? selectedSchedule.security ? '1' : '0' : '1',
            time: selectedSchedule?.time || null,
            days: selectedSchedule?.days || [1, 2, 3, 4, 5, 6, 7],
            comment: selectedSchedule?.comment || '',
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (values.time) {
                try {
                    setLoading(true)
                    let res: any

                    const obj = {
                        idObject,
                        days: values.days,
                        security: values.security === '1',
                        hour: values.time.hour,
                        minute: values.time.minute,
                        comment: values.comment,
                        controller,
                    }

                    if (selectedSchedule) {
                        res = await dispatch(changeObjectSchedule({
                            id: selectedSchedule.id,
                            ...obj,
                        }))
                    } else {
                        res = await dispatch(createObjectSchedule({
                            ...obj,
                        }))
                    }

                    if (res.payload) {
                        setSelectedSchedule(null)
                        setShowModalSchedule(false)
                    }
                } finally {
                    setLoading(false)
                }
            }
        },
    })
    const {values, handleChange, handleBlur, errors, touched, setFieldValue, validateForm, setTouched, setValues, resetForm, submitForm} = formik

    const currentValue: any = useMemo(() => {
        if (values.time) return dayjs(`${values.time.hour}:${values.time.minute}`, format)

        return undefined
    }, [values.time])

    const getValue = useCallback((value: any) => {
        if (value) {
            return {
                hour: value.hour(),
                minute: value.minute(),
            }
        }

        return null
    }, [])

    const daysCheck = useMemo(() => [1, 2, 3, 4, 5, 6, 7].map(i => (
        <div key={i} style={{display: 'flex', alignItems: 'center'}}>
            <div style={{width: 100}}>{getDay(i)}</div>
            <Checkbox checked={values.days.includes(i)}
                      onChange={() => {
                          if (values.days.includes(i)) setFieldValue('days', values.days.filter((f: number) => f !== i))
                          else {
                              const arr = [...values.days]
                              arr.push(i)
                              setFieldValue('days', arr)
                          }
                      }}
                      style={{marginLeft: 15}}
            />
        </div>
    )), [values.days])

    const onCancel = () => {
        setSelectedSchedule(null)
        setShowModalSchedule(false)
    }

    const onDelete = async () => {
        try {
            setLoading(true)
            const {payload}: any = await dispatch(deleteObjectSchedule(selectedSchedule.id))
            if (payload) {
                setOpen(false)
                setTimeout(() => onCancel(), 50)
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal title={`${selectedSchedule ? 'Изменить' : 'Новая'} запись`}
               style={{width: 'auto'}}
               centered
               open={showModalSchedule}
               okText="Сохранить"
               okButtonProps={{loading}}
               onCancel={onCancel}
               footer={<div style={{display: "flex", flexDirection: "row", gap: 10, justifyContent: "flex-end"}}>
                   <Button type="default" onClick={onCancel}>Отмена</Button>
                   {selectedSchedule
                       ? <Popconfirm title="Вы уверены что хотите удалить элемент расписания?"
                                     open={open}
                                     onConfirm={onDelete}
                                     okText="Да"
                                     cancelText="Нет"
                                     okButtonProps={{loading}}
                                     onCancel={() => setOpen(false)}
                       >
                           <Button type="default" danger onClick={() => setOpen(true)} loading={loading}>Удалить</Button>
                       </Popconfirm>
                       : null
                   }
                   <Button type="primary" loading={loading} onClick={() => submitForm()}>Сохранить</Button>
               </div>}
        >
            <div style={{display: 'flex', alignItems: 'center', gap: 40, margin: '20px 0'}}>
                <div>
                    {daysCheck}
                    {errors.days ? <div className="input-error-visible">{errors.days.toString()}</div> : null}
                </div>
                <div>
                    <FormSelect placeholder="Действие"
                                value={values.security}
                                onChange={(value) => setFieldValue('security', value)}
                                options={securityScheduleOptions}
                                className="form-select"
                    />
                    <TimePicker format={format}
                                style={{width: 160, marginTop: 10}}
                                onChange={(value: any) => setFieldValue('time', getValue(value))}
                                value={currentValue}
                                showNow={false}
                    />
                    {touched.time && errors.time ? <div className="input-error-visible">{errors.time.toString()}</div> : null}
                </div>
            </div>
            <FormTextarea placeholder="Комментарий"
                          value={values.comment}
                          name="comment"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          isInvalid={touched.comment && errors.comment}
                          allowClear
                          autoSize={{minRows: 2}}
            />
        </Modal>
    )
}